import { useState } from 'react';
import { Settings, Save, Megaphone, DatabaseBackup, CalendarCheck, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminSiteSettings({ settings, setSettings, albums, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [releaseResult, setReleaseResult] = useState(null);
  const [deletingStock, setDeletingStock] = useState(false);
  const [stockResult, setStockResult] = useState(null);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings`, settings);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await axios.post(`${API_URL}/admin/migrate-preview-data`, {});
      setMigrationResult(res.data.summary);
      toast.success('Migration complete! Refresh the page to see your data.');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Migration failed. Please try again.');
    } finally {
      setMigrating(false);
    }
  };

  const releaseThreeToday = async () => {
    if (!window.confirm('Set today\'s date as the release date for "Important", "Juss Leave" and "Hercules"? This unlocks full playback immediately.')) return;
    setReleasing(true);
    setReleaseResult(null);
    try {
      const res = await axios.post(`${API_URL}/admin/release-three-today`, {});
      setReleaseResult(res.data);
      const updatedCount = res.data.updated?.length || 0;
      toast.success(`Released ${updatedCount} track${updatedCount === 1 ? '' : 's'} today`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update release dates');
    } finally {
      setReleasing(false);
    }
  };

  const deleteStockVideos = async () => {
    if (!window.confirm('Delete demo/stock videos from your site? This only removes the placeholder videos, not any real videos you\'ve uploaded.')) return;
    setDeletingStock(true);
    setStockResult(null);
    try {
      const res = await axios.post(`${API_URL}/admin/delete-stock-videos`, {});
      setStockResult(res.data);
      toast.success(`Deleted ${res.data.deleted} stock video${res.data.deleted === 1 ? '' : 's'}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to delete stock videos');
    } finally {
      setDeletingStock(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Artist Info */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-purple-400" />
          Artist Information
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Artist Name</Label>
            <Input
              value={settings.artist_name}
              onChange={(e) => setSettings({ ...settings, artist_name: e.target.value })}
              placeholder="@DaRealQJones"
              className="input-dark mt-1"
            />
          </div>
          <div>
            <Label>Tagline</Label>
            <Input
              value={settings.artist_tagline}
              onChange={(e) => setSettings({ ...settings, artist_tagline: e.target.value })}
              placeholder="Independent Artist & Producer"
              className="input-dark mt-1"
            />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Hero Section</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Hero Title</Label>
            <Input
              value={settings.hero_title}
              onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
              placeholder="Liberation Vol. 1"
              className="input-dark mt-1"
            />
          </div>
          <div>
            <Label>Hero Description</Label>
            <Input
              value={settings.hero_description}
              onChange={(e) => setSettings({ ...settings, hero_description: e.target.value })}
              placeholder="Freedom is my birthright"
              className="input-dark mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Hero Image URL</Label>
            <Input
              value={settings.hero_image}
              onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })}
              placeholder="https://..."
              className="input-dark mt-1"
            />
          </div>
          <div>
            <Label>Featured Album</Label>
            <select
              value={settings.featured_album_id}
              onChange={(e) => setSettings({ ...settings, featured_album_id: e.target.value })}
              className="input-dark mt-1 w-full rounded-md"
            >
              <option value="">Select Album</option>
              {albums.map(album => (
                <option key={album.id} value={album.id}>{album.title}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Announcement */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-yellow-400" />
          Announcement Banner
        </h3>
        <div className="flex items-center gap-4 mb-4">
          <Switch
            checked={settings.announcement_enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, announcement_enabled: checked })}
          />
          <Label>Show announcement banner</Label>
        </div>
        <Input
          value={settings.announcement_text}
          onChange={(e) => setSettings({ ...settings, announcement_text: e.target.value })}
          placeholder="New album dropping October 15!"
          className="input-dark"
          disabled={!settings.announcement_enabled}
        />
      </div>

      {/* Social Links */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Social Links</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Instagram URL</Label>
            <Input
              value={settings.social_instagram}
              onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })}
              placeholder="https://instagram.com/..."
              className="input-dark mt-1"
            />
          </div>
          <div>
            <Label>Twitter/X URL</Label>
            <Input
              value={settings.social_twitter}
              onChange={(e) => setSettings({ ...settings, social_twitter: e.target.value })}
              placeholder="https://twitter.com/..."
              className="input-dark mt-1"
            />
          </div>
          <div>
            <Label>YouTube URL</Label>
            <Input
              value={settings.social_youtube}
              onChange={(e) => setSettings({ ...settings, social_youtube: e.target.value })}
              placeholder="https://youtube.com/..."
              className="input-dark mt-1"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button onClick={saveSettings} className="btn-primary w-full" data-testid="save-settings-button" disabled={saving}>
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Settings'}
      </button>

      {/* Restore Missing Data */}
      <div className="glass-card p-6 border border-yellow-500/30">
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <DatabaseBackup className="w-5 h-5 text-yellow-400" />
          Restore Missing Songs & Cover Art
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          If your tracks, albums or cover art are missing after deploying, click below to
          restore them from your last saved copy. This is safe to click multiple times —
          it only adds items that are missing and never overwrites or duplicates existing ones.
        </p>
        <button
          onClick={runMigration}
          className="btn-secondary w-full"
          data-testid="restore-missing-data-button"
          disabled={migrating}
        >
          <DatabaseBackup className="w-4 h-4 mr-2" />
          {migrating ? 'Restoring...' : 'Restore Missing Data'}
        </button>
        {migrationResult && (
          <div className="mt-4 text-sm text-gray-300 space-y-1" data-testid="restore-data-result">
            {Object.entries(migrationResult).map(([key, val]) => (
              <div key={key}>
                {key}: {val.inserted} restored, {val.skipped} already present
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Release 3 Tracks Today */}
      <div className="glass-card p-6 border border-green-500/30">
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-green-400" />
          Release "Important", "Juss Leave" &amp; "Hercules" Today
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          One-click action — sets today's UTC date as the release date for those three tracks so they
          unlock full playback across the site immediately. Safe to click again; it's idempotent.
        </p>
        <button
          onClick={releaseThreeToday}
          className="btn-primary w-full"
          data-testid="release-three-today-button"
          disabled={releasing}
        >
          <CalendarCheck className="w-4 h-4 mr-2" />
          {releasing ? 'Releasing...' : 'Release 3 Tracks Today'}
        </button>
        {releaseResult && (
          <div className="mt-4 text-sm text-gray-300 space-y-1" data-testid="release-three-result">
            <div>Updated: <strong className="text-green-300">{releaseResult.updated.join(', ') || 'none'}</strong></div>
            {releaseResult.not_found.length > 0 && (
              <div className="text-yellow-300">Not found: {releaseResult.not_found.join(', ')} — click "Restore Missing Data" first.</div>
            )}
            <div className="text-xs text-gray-500">Date used: {new Date(releaseResult.today).toLocaleDateString()}</div>
          </div>
        )}
      </div>

      {/* Delete Stock Videos */}
      <div className="glass-card p-6 border border-red-500/20">
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-red-400" />
          Delete Demo / Stock Videos
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          Removes any placeholder videos that came from the initial demo seed
          (leftover "Neon Genesis", "Behind the Scenes", etc.). Your uploaded videos are untouched.
        </p>
        <button
          onClick={deleteStockVideos}
          className="btn-secondary w-full"
          data-testid="delete-stock-videos-button"
          disabled={deletingStock}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {deletingStock ? 'Deleting...' : 'Delete Stock Videos'}
        </button>
        {stockResult && (
          <div className="mt-4 text-sm text-gray-300" data-testid="delete-stock-videos-result">
            Deleted <strong className="text-red-300">{stockResult.deleted}</strong> stock video{stockResult.deleted === 1 ? '' : 's'}.
            You have <strong className="text-white">{stockResult.remaining}</strong> video{stockResult.remaining === 1 ? '' : 's'} remaining.
          </div>
        )}
      </div>
    </div>
  );
}
